import React, { useState, useEffect, useMemo } from 'react';
import { change, reset, submit } from 'redux-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import * as $ from 'jquery';

import LabOrderForm from './LabOrderForm';
import { orderLoad } from '../../../../../../redux/actions/order/orderActions';
import Alert from '../../../../../../itrust_common/components/Alert';
import { humanize } from '../../../../../../itrust_common/components/HelperFunctions';
import { integrationLogsLoad } from '../../../../../../redux/actions/storeSettings/integrationLogsActions';
import {
  labOrderCreate,
  labOrderEditCancel,
  labOrderLoad,
  labOrderUpdate,
  sendToVisionWebCreate
} from '../../../../../../redux/actions/order/labOrderActions';

import VisionWebOrderForm from '../visionWebOrderForm/VisionWebOrderForm';
import VisionRxOrderForm from '../visionRxOrderForm/VisionRxOrderForm';

function LabOrderModal(props) {
  const {
    orderId,
    resourceType,
    resourceId,
    saving,
    orderSubmission,
    labOrderId,
    currentLabOrder: currentLabOrderProp,
    orderLabOrderId
  } = props;
  const dispatch = useDispatch();

  // Keep the tab identifiers consistent across the component.
  const [faitMessage, setFaitMessage] = useState('');

  const labOrderFormName = `labOrderForm-${resourceId}`;
  const visionWebFormName = `visionWebOrderForm-${resourceId}`;
  const visionRxFormName = `visionRxOrderForm-${resourceId}`;

  // Values used by the Fait submission flow.
  const labOrderFormValues =
    useSelector(state => state.form?.[labOrderFormName]?.values) || {};

  const currentLabOrder =
    useSelector(state => state.labOrder?.lab_order) || {};

  const visionWebFormValues =
    useSelector(state => state.form?.[visionWebFormName]?.values) || {};

  const visionRxFormValues =
    useSelector(state => state.form?.[visionRxFormName]?.values) || {};

  const storeSettings =
    useSelector(state => state.storeSetting?.store_settings || []);

  const filteredSettings =
    useSelector(state => state.storeSetting?.filtered_store_settings || {});

  // Vision Web settings.
  const visionWebSetting =
    storeSettings.find(
      item =>
        item.category === 'vision_web' &&
        item.store_config?.status === 'active'
    ) ||
    (filteredSettings.vision_web || []).find(
      item => item.store_config?.status === 'active'
    );

  const isEyeglass = resourceType === 'Orders::Eyeglass';

  const showVisionWeb = Boolean(visionWebSetting && isEyeglass);

  // VisionRx is shown for eyeglass orders when an active vision_rx setting exists.
  const visionRxSetting = storeSettings.find(
    item => item.category === 'vision_rx' && item.store_config?.status === 'active'
  );
  const showVisionRx = Boolean(visionRxSetting && isEyeglass);

const shouldShowVisionWebTabs =
  visionWebSetting &&
  resourceType === 'Orders::Eyeglass';

// Fait is available for contact lens orders and trial lens orders.
const shouldShowFaitTabs = ['Orders::ContactLens', 'Exam::Lens'].includes(resourceType);

// Fait settings.
const faitSetting =
  storeSettings.find(
    item =>
      item.category === 'fait' &&
      item.store_config?.status === 'active'
  ) || storeSettings.find(item => item.category === 'fait');

// Show Fait only for the matching order modal, unless configured for both.
const faitOrderSubmission = faitSetting?.store_config?.order_submission;

const currentOrderSubmission =
  orderSubmission ||
  (resourceType === 'Exam::Lens' ? 'trial_order' : 'revenue_order');

const shouldShowFaitTab =
  Boolean(faitSetting) &&
  shouldShowFaitTabs &&
  (
    faitOrderSubmission === 'both' ||
    faitOrderSubmission === orderSubmission ||
    faitOrderSubmission === currentOrderSubmission
  );

  const showTabs = showVisionWeb || showVisionRx || shouldShowFaitTab;

  const defaultTab = showVisionRx ? 'vision_rx' : 'fax';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Restore the selected tab and Fait message when editing an existing order.
  useEffect(() => {
    if (
      currentLabOrder?.submission_type === 'fait' &&
      shouldShowFaitTab
    ) {
      setActiveTab('fait');
      setFaitMessage(currentLabOrder?.message || '');
    } else if (currentLabOrder?.submission_type === 'vision_web') {
      setActiveTab('vision_web');
    } else if (currentLabOrder?.submission_type === 'vision_rx') {
      setActiveTab('vision_rx');
    } else {
      setActiveTab('fax');

      if (!currentLabOrder?.id) {
        setFaitMessage('');
      }
    }
  }, [
    currentLabOrder?.id,
    currentLabOrder?.submission_type,
    currentLabOrder?.message,
    shouldShowFaitTab
  ]);

  const [logVisionRxStatus, setLogVisionRxStatus] = useState(null);

  const extractStatusFromLog = (log) => {
    if (!log) return null;
    const versions = log.status_versions_attributes;
    if (Array.isArray(versions) && versions.length > 0) {
      const sorted = [...versions].sort((a, b) => {
        const timeA = new Date(a.created_at || 0).getTime();
        const timeB = new Date(b.created_at || 0).getTime();
        if (timeA !== timeB) return timeB - timeA;
        return (b.id || 0) - (a.id || 0);
      });
      const latest = sorted[0];
      const statusFromVersion =
        latest?.changed_values?.status?.after ||
        (typeof latest?.changed_values?.status === 'string' ? latest.changed_values.status : null) ||
        latest?.status;
      if (statusFromVersion) return statusFromVersion;
    }
    return log.status || null;
  };

  const integrationLogs = useSelector(state => state.integrationLogs?.logs || []);
  const matchingLog = useMemo(() => {
    if (!Array.isArray(integrationLogs) || integrationLogs.length === 0) return null;
    return (
      integrationLogs.find(
        item => String(item.orders_order_id || item.order_attributes?.id) === String(orderId)
      ) || integrationLogs[0]
    );
  }, [integrationLogs, orderId]);

  const visionRxStatus =
    currentLabOrder?.integration_log_attributes?.status ||
    logVisionRxStatus ||
    extractStatusFromLog(matchingLog);

  useEffect(() => {
    if (showVisionRx) {
      const payload = {
        page: 1,
        rpp: 50,
        include: '*',
        ...(orderId ? { search: orderId } : {}),
        filter: {
          integration_type: 'vision_rx',
        }
      };
      dispatch(integrationLogsLoad(payload))
        .then(res => {
          const list =
            res?.value?.data?.integration_logs ||
            res?.payload?.data?.integration_logs ||
            res?.data?.integration_logs ||
            [];
          const matched =
            list.find(
              item => String(item.orders_order_id || item.order_attributes?.id) === String(orderId)
            ) || list[0];
          const extracted = extractStatusFromLog(matched);
          if (extracted) {
            setLogVisionRxStatus(extracted);
          }
        })
        .catch(() => {});
    }
  }, [showVisionRx, orderId, dispatch]);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'finalized':
      case 'completed':
      case 'success':
      case 'sent':
        return 'badge-success text-white';
      case 'pending':
      case 'queued':
      case 'in_progress':
        return 'badge-warning text-dark';
      case 'unfinished':
        return 'badge-info text-white';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'badge-danger text-white';
      default:
        return 'badge-secondary text-white';
    }
  };

  const handleLabOrderEditCancel = () => {
    dispatch(labOrderEditCancel());
    setFaitMessage('');
    $(`#${labOrderFormName}-modal`).modal('hide');
  };

  const handleFaitSubmit = () => {
    const payload = {
      is_urgent:
        labOrderFormValues.is_urgent !== undefined
          ? labOrderFormValues.is_urgent
          : currentLabOrder.is_urgent || false,

      orders_order_id: orderId,

      contact_id:
        labOrderFormValues.contact_id ||
        currentLabOrder.contact_id ||
        faitSetting?.store_config?.contact_id ||
        faitSetting?.id,

      account_number:
        labOrderFormValues.account_number ||
        currentLabOrder.account_number ||
        faitSetting?.store_config?.account_number ||
        '',

      message:
        faitMessage ||
        labOrderFormValues.message ||
        currentLabOrder.message ||
        '',

      resource_type: resourceType,
      resource_id: resourceId,
      submission_type: 'fait'
    };

    dispatch(labOrderCreate(payload))
      .then(response => {
        handleAfterLabOrderSubmit(response, payload);
      })
      .catch(() => {});
  };

  const handleLabOrderSend = async ({ submit_to_abb } = {}) => {
    if (submit_to_abb) {
      await dispatch(
        change(labOrderFormName, 'submit_to_abb', submit_to_abb)
      );
    } else {
      await dispatch(change(labOrderFormName, 'send_fax', true));
    }

    dispatch(submit(labOrderFormName));
  };

  const handleVisionWebSend = () => {
    const payload = {
      orders_order_id: orderId,
      submission_type: 'vision_web',
      resource_type: resourceType,
      resource_id: resourceId,
      send_to_visionweb: true,

      integration_details: {
        bill_account: visionWebFormValues.bill_account,
        ship_account: visionWebFormValues.ship_account,
        job_type: visionWebFormValues.job_type,
        message_to_lab: visionWebFormValues.message_to_lab,

        location_id: visionWebFormValues.location_id
          ? Number(visionWebFormValues.location_id)
          : undefined
      }
    };

    dispatch(sendToVisionWebCreate(payload))
      .then(() => handleLabOrderFormClose())
      .catch(() => {});
  };

  const handleVisionRxSend = () => {
    const payload = {
      orders_order_id: orderId,
      submission_type: 'vision_rx',
      resource_type: resourceType,
      resource_id: resourceId,
      send_to_visionrx: true,

      integration_details: {
        message_to_lab: visionRxFormValues.message_to_lab || ''
      }
    };

    dispatch(sendToVisionWebCreate(payload))
      .then(() => handleLabOrderFormClose())
      .catch(() => {});
  };

  useEffect(() => {
    if (showVisionRx && (currentLabOrder?.submission_type === 'vision_rx' || !currentLabOrder?.submission_type)) {
      setActiveTab('vision_rx');
    }
  }, [showVisionRx, currentLabOrder?.submission_type]);

  useEffect(() => {
    if (activeTab === 'vision_rx') {
      const idToLoad = labOrderId
      if (idToLoad) {
        dispatch(labOrderLoad(idToLoad, { include: '*', id: idToLoad }));
      }
    }
  }, [activeTab, labOrderId, currentLabOrder?.id, orderLabOrderId, resourceId, dispatch]);

  useEffect(() => {
    const modalId = `#${labOrderFormName}-modal`;
    const handleModalShow = () => {
      if (showVisionRx) {
        setActiveTab('vision_rx');
        const idToLoad = labOrderId
        if (idToLoad) {
          dispatch(labOrderLoad(idToLoad, { include: '*', id: idToLoad }));
        }
      }
    };
    $(modalId).on('show.bs.modal', handleModalShow);
    return () => {
      $(modalId).off('show.bs.modal', handleModalShow);
    };
  }, [labOrderFormName, showVisionRx, labOrderId, currentLabOrder?.id, orderLabOrderId, resourceId, dispatch]);

  const handleVisionRxTabClick = () => {
    setActiveTab('vision_rx');
    const idToLoad = labOrderId 
    if (idToLoad) {
      dispatch(labOrderLoad(idToLoad, { include: '*', id: idToLoad }));
    }
  };

  const handleLabOrderSubmit = data => {
    const payload = {
      ...data,
      resource_type: resourceType,
      resource_id: resourceId,
      orders_order_id: orderId,
      submission_type: 'fax'
    };

    if (payload.send_fax && !payload.contact_id) {
      Alert.warning(
        'To send Lab Order Secure Fax need to be selected.'
      );

      dispatch(change(labOrderFormName, 'send_fax', false));
      return;
    }

    const request =
      payload.id !== undefined
        ? dispatch(labOrderUpdate(payload))
        : dispatch(labOrderCreate(payload));

    request
      .then(response => handleAfterLabOrderSubmit(response, payload))
      .catch(() => {
        dispatch(change(labOrderFormName, 'send_fax', false));
      });
  };

  const handleAfterLabOrderSubmit = (response, data) => {
    dispatch(orderLoad(orderId, { include: '*' }));

    const url =
      response?.value?.data?.lab_order?.pdf_attributes?.file?.url;

    if (url) {
      window.open(url, '_blank');
    }

    if (
      data.send_fax ||
      data.send_to_visionweb ||
      data.send_to_visionrx ||
      data.submission_type === 'fait'
    ) {
      handleLabOrderFormClose();
    }
  };

  const handleLabOrderFormClose = () => {
    $(`#${labOrderFormName}-modal`).modal('hide');

    dispatch(reset(labOrderFormName));
    dispatch(reset(visionWebFormName));
    dispatch(reset(visionRxFormName));

    setFaitMessage('');
    setActiveTab('fax');

    dispatch(labOrderEditCancel());
  };

  const renderHeaderActions = () => (
    <div className="d-flex align-items-center">
      <button
        type="button"
        className="btn btn-outline-secondary mr-3"
        onClick={handleLabOrderEditCancel}
      >
        Cancel
      </button>

      {activeTab === 'fax' && (
        <>
          <button
            type="button"
            className="btn btn-outline-primary mr-3"
            onClick={() => handleLabOrderSend()}
            disabled={saving}
          >
            Send
          </button>

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => dispatch(submit(labOrderFormName))}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save and Create'}
          </button>
        </>
      )}

      {activeTab === 'vision_web' && (
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleVisionWebSend}
          disabled={saving}
        >
          {saving ? 'Sending...' : 'Send to Vision Web'}
        </button>
      )}

      {activeTab === 'vision_rx' && (
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleVisionRxSend}
          disabled
        >
          {saving ? 'Sending...' : 'Send to VisionRx'}
        </button>
      )}

      {activeTab === 'fait' && (
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleFaitSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save and Create'}
        </button>
      )}
    </div>
  );

  // Common tab renderer for all integrations.
  // `onSelect` is optional; VisionRx uses it to also load the lab order on click.
  const renderTab = (tab, label, targetId, onSelect) => (
    <li
      className="nav-item nav-items"
      onClick={onSelect || (() => setActiveTab(tab))}
      key={tab}
    >
      <a
        className={`p-3 mr-3 tab-link ${
          activeTab === tab ? 'active full-bg-green' : ''
        }`}
        href={`#${targetId}`}
        data-toggle="tab"
        role="tab"
        onClick={event => event.preventDefault()}
      >
        {label}
      </a>
    </li>
  );

  return (
    <div className="inner-modal">
      <div
        className="modal fade popper overflow-auto"
        id={`${labOrderFormName}-modal`}
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-md" role="document">
          <div className="modal-content border-0 rounded shadow-lg">
            <div className="modal-header align-items-center border-bottom">
              <h5 className="text-dark mb-0">Lab Order</h5>
              {renderHeaderActions()}
            </div>

            {showTabs && (
              <div className="px-5 pt-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="text-nowrap overflow-auto border border-radius-6 px-1 py-2 bg-white d-inline-block">
                    <ul
                      className="nav nav-pills p-3 border-0 mb-0"
                      role="tablist"
                    >
                      {renderTab('fax', 'Fax', 'lab-order')}

                      {showVisionWeb &&
                        renderTab(
                          'vision_web',
                          'Vision Web',
                          'vision-web'
                        )}

                      {showVisionRx &&
                        renderTab(
                          'vision_rx',
                          'VisionRx',
                          'vision-rx',
                          handleVisionRxTabClick
                        )}

                      {shouldShowFaitTab &&
                        renderTab('fait', 'Fait', 'fait')}
                    </ul>
                  </div>

                  {showVisionRx && activeTab === 'vision_rx' && visionRxStatus && (
                    <div className="d-flex align-items-center ml-3 my-1">
                      <span className="text-muted mr-2 font-size-13 font-weight-500">Status:</span>
                      <span
                        className={`badge badge-pill ${getStatusBadgeClass(visionRxStatus)} text-capitalize px-3 py-2 font-size-12`}
                      >
                        {humanize(visionRxStatus)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="tab-content">
              <div
                id="lab-order"
                className={`tab-pane fade ${
                  !showTabs || activeTab === 'fax'
                    ? 'show active'
                    : ''
                }`}
              >
                <LabOrderForm
                  resourceId={resourceId}
                  resourceType={resourceType}
                  onSubmit={handleLabOrderSubmit}
                  form={labOrderFormName}
                />
              </div>

              {showVisionWeb && (
                <div
                  id="vision-web"
                  className={`tab-pane fade ${
                    activeTab === 'vision_web' ? 'show active' : ''
                  }`}
                >
                  <VisionWebOrderForm form={visionWebFormName} />
                </div>
              )}

              {showVisionRx && (
                <div
                  id="vision-rx"
                  className={`tab-pane fade ${
                    activeTab === 'vision_rx' ? 'show active' : ''
                  }`}
                >
                  <VisionRxOrderForm
                    form={visionRxFormName}
                    integrationLogAttributes={currentLabOrder?.integration_log_attributes}
                  />
                </div>
              )}

              {shouldShowFaitTab && (
                <div
                  id="fait"
                  className={`tab-pane fade ${
                    activeTab === 'fait' ? 'show active' : ''
                  }`}
                >
                  <div className="col-12 form-group">
                    <label htmlFor={`fait-message-${resourceId}`}>
                      Message to Fait
                    </label>

                    <textarea
                      id={`fait-message-${resourceId}`}
                      name="message"
                      rows={5}
                      className="form-control"
                      placeholder="Enter message to Fait..."
                      value={faitMessage}
                      onChange={event =>
                        setFaitMessage(event.target.value)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const {
    labOrder,
    order: { order }
  } = state;
  const eyeglasses = order?.eyeglasses || order?.eyeglasses_attributes || [];
  const contactLenses = order?.contact_lenses || order?.contact_lenses_attributes || [];
  const relatedItem = [...eyeglasses, ...contactLenses].find(item => item?.id === ownProps?.resourceId);

  return {
    saving: labOrder.saving,
    orderId: order?.id,
    currentLabOrder: labOrder?.lab_order,
    orderLabOrderId: relatedItem?.lab_order_id
  };
};

export default connect(mapStateToProps)(LabOrderModal);